'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Pencil } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Label } from '@/shared/ui/shadcn/label';
import { typographyVariants } from '@/shared/ui/shadcn/typography';

type TextVariant = 'body' | 'heading-1' | 'heading-2' | 'heading-3' | 'small';

interface EditableTextProps {
  value: string | null | undefined;
  onSave: (value: string | null) => Promise<void>;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  editClassName?: string;
  showEditIcon?: boolean;
  label?: string;
  variant?: TextVariant;
  maxLength?: number;
  rows?: number; // Reserved for future use
  disabled?: boolean;
}

export function EditableText({
  value,
  onSave,
  placeholder: placeholderProp,
  multiline = false,
  className,
  editClassName,
  showEditIcon = true,
  label,
  variant = 'body',
  maxLength,
  rows: _rows,
  disabled = false,
}: EditableTextProps) {
  const tCommon = useTranslations('common');
  const placeholder = placeholderProp ?? tCommon('edit');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editableRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const originalValueRef = useRef<string>(value || '');
  const [inputWidth, setInputWidth] = useState('100%');
  const measureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isEditing) {
      if (multiline && editableRef.current) {
        // For multiline contenteditable
        editableRef.current.focus();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else if (!multiline && inputRef.current) {
        // For single-line input
        inputRef.current.focus();
        inputRef.current.select();
        adjustInputWidth();
      }
    }
  }, [isEditing, multiline]);

  // Auto-resize input based on content (for single-line only)
  const adjustInputWidth = () => {
    if (!multiline && measureRef.current && inputRef.current) {
      const width = measureRef.current.offsetWidth;
      // Add padding for comfortable editing
      setInputWidth(`${Math.max(width + 30, 150)}px`);
    }
  };

  useEffect(() => {
    if (!multiline && inputRef.current) {
      adjustInputWidth();
    }
  }, [inputRef.current?.value, multiline]);

  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      let newValue: string | null = null;

      if (multiline && editableRef.current) {
        newValue = editableRef.current.textContent?.trim() || null;
      } else if (!multiline && inputRef.current) {
        newValue = inputRef.current.value.trim() || null;
      }

      await onSave(newValue);
      originalValueRef.current = newValue || '';
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // Restore original value
      if (multiline && editableRef.current) {
        editableRef.current.textContent = originalValueRef.current;
      } else if (!multiline && inputRef.current) {
        inputRef.current.value = originalValueRef.current;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Restore original value
      if (multiline && editableRef.current) {
        editableRef.current.textContent = originalValueRef.current;
      } else if (!multiline && inputRef.current) {
        inputRef.current.value = originalValueRef.current;
      }
      setIsEditing(false);
    }
  };

  const handleInput = () => {
    // Enforce max length if specified
    if (maxLength && editableRef.current) {
      const text = editableRef.current.textContent || '';
      if (text.length > maxLength) {
        // Prevent further input
        editableRef.current.textContent = text.slice(0, maxLength);
        // Move cursor to end
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
    selection.collapseToEnd();
  };

  // Get variant-specific styling using Typography system
  const getVariantStyles = (variant: TextVariant) => {
    switch (variant) {
      case 'heading-1':
        return typographyVariants({ variant: 'h1' });
      case 'heading-2':
        return typographyVariants({ variant: 'h2' });
      case 'heading-3':
        return typographyVariants({ variant: 'h3' });
      case 'small':
        return typographyVariants({ variant: 'muted' });
      case 'body':
      default:
        return typographyVariants({ variant: 'p' });
    }
  };

  const displayValue = value || '';

  const handleClick = () => {
    if (!isEditing && !disabled) {
      originalValueRef.current = value || '';
      setIsEditing(true);
    }
  };

  if (isEditing && !multiline) {
    // Single-line editing with input element
    return (
      <div className={className}>
        {label && <Label className="mb-1 block">{label}</Label>}
        <div
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2',
            'bg-accent/50'
          )}
        >
          <div className={cn('flex-1 relative')}>
            {/* Hidden span to measure text width */}
            <span
              ref={measureRef}
              className={cn(
                getVariantStyles(variant),
                'invisible absolute whitespace-nowrap'
              )}
              style={{ font: 'inherit' }}
            >
              {inputRef.current?.value || placeholder}
            </span>
            <input
              ref={inputRef}
              type="text"
              defaultValue={value || ''}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              placeholder={placeholder}
              maxLength={maxLength}
              className={cn(
                getVariantStyles(variant),
                'bg-transparent border-0 outline-none focus:outline-none focus:ring-0',
                editClassName
              )}
              style={{
                font: 'inherit',
                width: inputWidth,
              }}
            />
          </div>
          {showEditIcon && <div className="w-4 h-4 flex-shrink-0 mt-0.5" />}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('group', !disabled && 'cursor-pointer', className)}
      onClick={handleClick}
    >
      {label && <Label className="mb-1 block">{label}</Label>}
      <div
        className={cn(
          'flex items-start gap-2 rounded-md px-3 py-2',
          'transition-all duration-200',
          'hover:bg-accent/50',
          isEditing && 'bg-accent/50'
        )}
      >
        <div
          className={cn(
            'flex-1',
            getVariantStyles(variant),
            multiline && 'whitespace-pre-wrap'
          )}
        >
          {multiline ? (
            <div
              ref={editableRef}
              contentEditable={isEditing}
              suppressContentEditableWarning={true}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              onPaste={handlePaste}
              className={cn(
                'outline-none',
                !value && !isEditing && 'text-muted-foreground',
                editClassName
              )}
              style={{
                minHeight: '1em',
                wordBreak: 'break-word',
              }}
            >
              {!value && !isEditing ? placeholder : displayValue}
            </div>
          ) : !value && !isEditing ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            displayValue
          )}
        </div>
        {showEditIcon && !disabled && (
          <Pencil
            className={cn(
              'h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              'touch:opacity-100', // Always visible on touch devices
              isEditing && 'opacity-100'
            )}
          />
        )}
      </div>
    </div>
  );
}
